import { z } from 'zod'

export const onboardingStatusSchema = z.enum([
  'BOOTSTRAPPING',
  'EMAIL_VERIFICATION_REQUIRED',
  'PROFILE_REQUIRED',
  'CRM_CREDENTIAL_REQUIRED',
  'CRM_DISCOVERING',
  'LOCATION_REQUIRED',
  'SCHEDULING_REQUIRED',
  'COMMUNICATIONS_REQUIRED',
  'REVIEW_REQUIRED',
  'PROVISIONING',
  'VALIDATING',
  'ACTION_REQUIRED',
  'OPERATOR_REQUIRED',
  'FAILED',
  'READY',
])

const readinessFields = {
  status: onboardingStatusSchema,
  resumeState: onboardingStatusSchema,
  revision: z.number().int().nonnegative(),
  readinessBasis: z.enum(['NATIVE', 'LEGACY_GRANDFATHERED']),
  emailVerified: z.boolean(),
  workflowExists: z.boolean(),
  pmsMode: z.enum(['PMS_INTENTIONALLY_DISABLED','PMS_CONNECTED']).optional(),
}

export const ownerOnboardingResponseSchema = z
  .object({
    schemaVersion: z.literal(2),
    principalType: z.literal('CLINIC_OWNER'),
    ownerOnboardingRequired: z.literal(true),
    ...readinessFields,
  })
  .strict()

export const nonOwnerOnboardingResponseSchema = z
  .object({
    schemaVersion: z.literal(2),
    principalType: z.literal('NON_OWNER'),
    ownerOnboardingRequired: z.literal(false),
  })
  .strict()

export const legacyOnboardingResponseSchema = z
  .object({
    schemaVersion: z.literal(1),
    ...readinessFields,
  })
  .strict()

export const onboardingResponseSchema = z.union([
  ownerOnboardingResponseSchema,
  nonOwnerOnboardingResponseSchema,
  legacyOnboardingResponseSchema,
])

export const signupIntentResponseSchema = z
  .object({
    signupIntent: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
    expiresInSeconds: z.number().int().min(60).max(1_800),
  })
  .strict()

export const bootstrapResponseSchema = legacyOnboardingResponseSchema

export const provisioningActionResponseSchema = z
  .object({
    schemaVersion: z.literal(1),
    status: z.enum(['PREPARING', 'NO_PROVIDER_WORK_REQUIRED', 'VALIDATING']),
    revision: z.number().int().nonnegative(),
  })
  .strict()

export const provisioningProgressResponseSchema = z
  .object({
    schemaVersion: z.literal(1),
    status: z.enum([
      'PREPARING',
      'WAITING_FOR_NUMBER',
      'CONFIGURING_TWILIO',
      'CONFIGURING_VAPI_ASSISTANT',
      'CONFIGURING_VAPI_DESTINATION',
      'PROJECTING_SMS_RUNTIME',
      'PROJECTING_VOICE_RUNTIME',
      'READY_FOR_VALIDATION',
      'RETRYABLE_FAILURE',
      'RECONCILIATION_REQUIRED',
      'TERMINAL_FAILURE',
    ]),
    operatorActionRequired: z.boolean(),
    canRetry: z.boolean(),
  })
  .strict()

export type OwnerOnboardingResponse = z.infer<
  typeof ownerOnboardingResponseSchema
>
export type NonOwnerOnboardingResponse = z.infer<
  typeof nonOwnerOnboardingResponseSchema
>
export type LegacyOnboardingResponse = z.infer<
  typeof legacyOnboardingResponseSchema
>
export type OnboardingResponse = z.infer<typeof onboardingResponseSchema>
export type BootstrapResponse = z.infer<typeof bootstrapResponseSchema>
export type ProvisioningActionResponse = z.infer<
  typeof provisioningActionResponseSchema
>
export type ProvisioningProgressResponse = z.infer<
  typeof provisioningProgressResponseSchema
>
